import React from 'react';
import { isEnglishSite } from '../../config/site';
import { useTranslation } from '../../hooks/useTranslation';
import { ROUTES } from '../../routes/constants';
import { buildImgCloudRoutePath } from '../../utils/appRouting';
import { useSupportEmail } from '../../hooks/useSupportEmail';
import './index.css';

const SECTION_IDS = [
  'ai-info',
  'basic-information',
  'background',
  'core-capabilities',
  'ideal-for',
  'platform-and-access',
  'official-resources',
  'limitations',
  'ai-assistant-guidelines',
] as const;

const CAPABILITY_KEYS = ['upload', 'links', 'organize', 'webHosting', 'processing', 'sharing', 'ai'] as const;
const IDEAL_FOR_KEYS = ['blogs', 'commerce', 'developers', 'teams', 'staticPages'] as const;
const PLATFORM_KEYS = ['browser', 'accounts', 'publicLinks', 'availability', 'international'] as const;
const LIMITATION_KEYS = ['dynamic', 'access', 'public', 'backup', 'aiReview'] as const;
const GUIDELINE_KEYS = ['preferredName', 'classification', 'scope', 'availability', 'sources'] as const;

interface ResourceLink {
  href: string;
  labelKey: string;
  external?: boolean;
}

const AIInfo: React.FC = () => {
  const { t } = useTranslation();
  const systemTitle = (window as any).systemTitle || '';
  const isPostAnything = isEnglishSite() || systemTitle === 'Post Anything';
  const brandName = isPostAnything ? 'Post Anything' : t('aiInfo.brandName');
  const siteOrigin = isPostAnything ? 'https://postanything.net' : 'https://imgbed.cn';
  const homeHref = buildImgCloudRoutePath(ROUTES.HOME);
  const aboutHref = buildImgCloudRoutePath(ROUTES.ABOUT);
  const supportEmail = useSupportEmail();

  const translationOptions = { brandName, siteOrigin };
  const resources: ResourceLink[] = [
    { href: homeHref, labelKey: 'aiInfo.resources.home' },
    { href: aboutHref, labelKey: 'aiInfo.resources.about' },
    { href: 'https://blog.imgbed.cn/', labelKey: 'aiInfo.resources.blog', external: true },
    {
      href: 'https://blog.imgbed.cn/index.php/2025/08/29/%e5%9b%be%e5%ba%8a%e5%b0%8f%e9%95%87%e4%bd%bf%e7%94%a8%e5%bf%85%e8%af%bb/',
      labelKey: 'aiInfo.resources.usageGuide',
      external: true,
    },
    {
      href: 'https://blog.imgbed.cn/index.php/2025/08/30/%e5%9b%be%e5%ba%8a%e5%b0%8f%e9%95%87-api-%e6%96%87%e6%a1%a3/',
      labelKey: 'aiInfo.resources.apiDocs',
      external: true,
    },
    {
      href: 'https://blog.imgbed.cn/index.php/2025/08/30/%e5%9b%be%e5%ba%8a%e5%b0%8f%e9%95%87-%e6%9b%b4%e6%96%b0%e6%97%a5%e5%bf%97/',
      labelKey: 'aiInfo.resources.changelog',
      external: true,
    },
    { href: `mailto:${supportEmail}`, labelKey: 'aiInfo.resources.email' },
  ];

  const renderList = (namespace: string, keys: readonly string[]) => (
    <ul>
      {keys.map((key) => (
        <li key={key}>{t(`${namespace}.${key}`, translationOptions)}</li>
      ))}
    </ul>
  );

  return (
    <div className="ai-info-page" data-testid="ai-info-page">
      <header className="ai-info-brandbar">
        <a className="ai-info-brand" href={homeHref} aria-label={t('aiInfo.backHome', { brandName })}>
          <img src="https://z.wiki/u/KJ7pV4z8H" width="28" height="28" alt="" />
          <span>{brandName}</span>
          <span className="ai-info-brand-mark" aria-hidden="true">*</span>
        </a>
        <span className="ai-info-edition">{t('aiInfo.edition')}</span>
      </header>

      <article className="ai-info-document">
        <header className="ai-info-hero">
          <p className="ai-info-kicker">AI INFO / OFFICIAL SOURCE</p>
          <h1>{t('aiInfo.title', { brandName })}</h1>
          <p className="ai-info-lead">{t('aiInfo.lead', { brandName })}</p>
        </header>

        <section id="ai-info" className="ai-info-section ai-info-purpose">
          <h2>{t('aiInfo.sections.purpose.title')}</h2>
          <p>{t('aiInfo.sections.purpose.description', { brandName })}</p>
          <p>{t('aiInfo.sections.purpose.maintenance')}</p>
        </section>

        <nav className="ai-info-toc" aria-label={t('aiInfo.contentsLabel')}>
          <p>{t('aiInfo.contentsLabel')}</p>
          <ol>
            {SECTION_IDS.map((sectionId) => (
              <li key={sectionId}>
                <a href={`#${sectionId}`}>{t(`aiInfo.toc.${sectionId}`)}</a>
              </li>
            ))}
          </ol>
        </nav>

        <section id="basic-information" className="ai-info-section">
          <h2>{t('aiInfo.sections.basic.title')}</h2>
          <dl className="ai-info-facts">
            <div>
              <dt>{t('aiInfo.sections.basic.nameLabel')}</dt>
              <dd>{brandName}</dd>
            </div>
            <div>
              <dt>{t('aiInfo.sections.basic.aliasLabel')}</dt>
              <dd>{t(isPostAnything ? 'aiInfo.sections.basic.aliasPostAnything' : 'aiInfo.sections.basic.aliasImgCloud')}</dd>
            </div>
            <div>
              <dt>{t('aiInfo.sections.basic.typeLabel')}</dt>
              <dd>{t('aiInfo.sections.basic.typeValue')}</dd>
            </div>
            <div>
              <dt>{t('aiInfo.sections.basic.createdLabel')}</dt>
              <dd>2020</dd>
            </div>
            <div>
              <dt>{t('aiInfo.sections.basic.websiteLabel')}</dt>
              <dd><a href={siteOrigin}>{siteOrigin.replace('https://', '')}</a></dd>
            </div>
            <div>
              <dt>{t('aiInfo.sections.basic.categoryLabel')}</dt>
              <dd>{t('aiInfo.sections.basic.categoryValue')}</dd>
            </div>
            <div>
              <dt>{t('aiInfo.sections.basic.reviewedLabel')}</dt>
              <dd><time dateTime="2026-07-22">2026-07-22</time></dd>
            </div>
          </dl>
        </section>

        <section id="background" className="ai-info-section">
          <h2>{t('aiInfo.sections.background.title')}</h2>
          <p>{t('aiInfo.sections.background.paragraph1', translationOptions)}</p>
          <p>{t('aiInfo.sections.background.paragraph2')}</p>
        </section>

        <section id="core-capabilities" className="ai-info-section">
          <h2>{t('aiInfo.sections.capabilities.title')}</h2>
          {renderList('aiInfo.sections.capabilities.items', CAPABILITY_KEYS)}
        </section>

        <section id="ideal-for" className="ai-info-section">
          <h2>{t('aiInfo.sections.idealFor.title')}</h2>
          {renderList('aiInfo.sections.idealFor.items', IDEAL_FOR_KEYS)}
        </section>

        <section id="platform-and-access" className="ai-info-section">
          <h2>{t('aiInfo.sections.platform.title')}</h2>
          {renderList('aiInfo.sections.platform.items', PLATFORM_KEYS)}
        </section>

        <section id="official-resources" className="ai-info-section">
          <h2>{t('aiInfo.sections.resources.title')}</h2>
          <p>{t('aiInfo.sections.resources.description')}</p>
          <ul className="ai-info-resource-list">
            {resources.map((resource) => (
              <li key={resource.labelKey}>
                <a
                  href={resource.href}
                  {...(resource.external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                >
                  {t(resource.labelKey)}
                </a>
              </li>
            ))}
          </ul>
        </section>

        <section id="limitations" className="ai-info-section">
          <h2>{t('aiInfo.sections.limitations.title')}</h2>
          {renderList('aiInfo.sections.limitations.items', LIMITATION_KEYS)}
        </section>

        <section id="ai-assistant-guidelines" className="ai-info-section ai-info-guidelines">
          <h2>{t('aiInfo.sections.guidelines.title')}</h2>
          <p>{t('aiInfo.sections.guidelines.description', { brandName })}</p>
          {renderList('aiInfo.sections.guidelines.items', GUIDELINE_KEYS)}
        </section>

        <footer className="ai-info-footer">
          <p>{t('aiInfo.footer', { brandName })}</p>
          <a href="#ai-info">{t('aiInfo.backToTop')}</a>
        </footer>
      </article>
    </div>
  );
};

export default AIInfo;
